import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { generatePath, useHistory, useParams } from "react-router-dom";
import { getOrganizationUser } from "@/api/endpoints/organization";
import { getTimezones } from "@/api/endpoints/timezones";
import { ROUTES } from "@/routes_new";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@components/ui/sheet";
import { Skeleton } from "@components/ui/skeleton";
import ErrorComponent from "@components/ui/error";
import { Trans, useLingui } from "@lingui/react/macro";
import { Avatar, AvatarFallback } from "@components/ui/avatar";
import UserForm from "./partials/UserForm";
import SearchSubscriptionsTable from "../../../email/partials/SearchSubscriptionsTable";
import ReportSubscriptionsTable from "../../../email/partials/ReportSubscriptionsTable";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import { useOrganization } from "@/hooks/useOrganization";

export default function UserSheet() {
  const { t } = useLingui();

  // URL state
  const history = useHistory();
  const { orgUuid, userId } = useParams<{ orgUuid: string; userId: string }>();

  // Local state
  const [isOpen, setIsOpen] = useState(true);

  // Server state
  const {
    data: userData,
    error: userError,
    isLoading: isUserLoading,
  } = useQuery({
    queryKey: [orgUuid, "users", userId],
    queryFn: () => {
      return getOrganizationUser({
        organization_uuid: orgUuid,
        user_id: Number(userId),
      });
    },
  });

  const userName = userData?.ok ? userData.data.name : undefined;

  const { data: orgData } = useOrganization();
  const allowedIntervals = orgData?.ok
    ? orgData.data.plan?.notification_subscription_intervals
    : undefined;

  const {
    data: timezonesData,
    error: timezonesError,
    isLoading: isTimezonesLoading,
  } = useQuery({
    queryKey: ["timezones"],
    queryFn: getTimezones,
  });

  // Handlers
  function handleOpenChange(open: boolean) {
    if (!open) {
      setIsOpen(false);
      setTimeout(() => {
        history.push(generatePath(ROUTES.SETTINGS_TEAM_ORGID, { orgUuid }));
      }, 300);
    }
  }

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent className="overflow-y-auto pb-32">
        {userData && timezonesData ? (
          userData.ok && timezonesData.ok ? (
            <>
              <SheetHeader>
                <Avatar>
                  <AvatarFallback>
                    {userData.data.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>

                <SheetTitle className="mt-2">{userData.data.name}</SheetTitle>
                <SheetDescription>
                  <Trans>Redigera användarens uppgifter</Trans>
                </SheetDescription>
              </SheetHeader>

              <UserForm
                orgUuid={orgUuid}
                user={userData.data}
                timezones={timezonesData.data}
              />

              <div className="space-y-4 p-6">
                <Card className="gap-3 pb-0 pt-5">
                  <CardHeader className="px-4 gap-3">
                    <CardTitle>
                      <Trans>Bevakningsinställningar</Trans>
                    </CardTitle>
                    <CardDescription>
                      <Trans>
                        Välj vilka bevakningar <strong>{userName}</strong> ska
                        prenumerera på, och på vilket tidsintervall.
                      </Trans>
                    </CardDescription>
                  </CardHeader>

                  <SearchSubscriptionsTable
                    isShowingSubscribers={false}
                    orgUuid={orgUuid}
                    userId={userData.data.id}
                    allowedIntervals={allowedIntervals}
                  />
                </Card>

                <Card className="gap-2 pb-0 pt-5">
                  <CardHeader className="px-4 gap-3">
                    <CardTitle>
                      <Trans>Rapportinställningar</Trans>
                    </CardTitle>
                    <CardDescription>
                      <Trans>
                        Välj vilka rapporter <strong>{userName}</strong> ska
                        prenumerera på.
                      </Trans>
                    </CardDescription>
                  </CardHeader>

                  <ReportSubscriptionsTable
                    isShowingSubscribers={false}
                    orgUuid={orgUuid}
                    userId={userData.data.id}
                  />
                </Card>
              </div>
            </>
          ) : (
            <ErrorComponent
              heading={t`Åh nej!`}
              description={t`Något gick fel när vi försökte ladda in användarens uppgifter.`}
            />
          )
        ) : isUserLoading || isTimezonesLoading ? (
          <>
            <SheetHeader className="gap-3.5">
              <Skeleton className="size-8 rounded-full" />
              <Skeleton className="w-24 h-6" />
              <Skeleton className="w-64 h-5" />
            </SheetHeader>

            <div className="flex flex-col gap-7 px-4 pb-4">
              {Array.from(Array(6).keys()).map((n) => (
                <div key={n} className="space-y-2">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-10" />
                </div>
              ))}

              <div className="flex items-center justify-between">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-5 w-12" />
              </div>

              <Skeleton className="h-9" />
            </div>
          </>
        ) : userError || timezonesError ? (
          <ErrorComponent
            heading={t`Åh nej!`}
            error={userError || timezonesError}
            description={t`Något gick fel när vi försökte ladda in användarens uppgifter.`}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
